import { useCallback, useEffect, useRef, useState } from 'react';
import { pgMonitorRequest, refreshOutcome } from '../../Admin/api/pgSettlementMonitor';

const statuses = ['WARNING', 'REVIEW_REQUIRED', 'WAITING', 'WITHIN_TOLERANCE_ESTIMATE', 'DISABLED'];
export default function usePgMonitor(active, polling) {
  const [config, setConfig] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const configRef = useRef(null);
  const task = useRef(null);
  const alive = useRef(true);
  const visibleScope = useRef(polling);
  visibleScope.current = polling;
  useEffect(() => { alive.current = true; return () => { alive.current = false; task.current?.abort(); }; }, []);
  const run = useCallback(async (manual = false, forceConfig = false, configOnly = false) => {
    if (!active || document.hidden || task.current) return false;
    const controller = new AbortController();
    let finish; controller.finished = new Promise((resolve) => { finish = resolve; });
    task.current = controller; setBusy(true);
    const valid = () => alive.current && !controller.signal.aborted;
    const call = (path, method = 'GET') => pgMonitorRequest(path, method, undefined, undefined, controller.signal);
    const readConfig = async () => {
      const cfg = await call('/config');
      if (typeof cfg?.enabled !== 'boolean') throw Error('설정 확인 실패');
      if (valid()) { configRef.current = cfg; setConfig(cfg); }
      return cfg;
    };
    try {
      const cfg = forceConfig || !configRef.current ? await readConfig() : configRef.current;
      if (!valid()) return false;
      if (!cfg.enabled) { setResult(null); setError(''); return true; }
      if (configOnly) return true;
      let operationError = '';
      if (manual) {
        setNotice('');
        try {
          const refreshed = await call('/refresh', 'POST');
          if (!valid()) return false;
          const outcome = refreshOutcome(refreshed);
          setNotice(outcome.message);
          if (outcome.disabled) { await readConfig(); return false; }
          if (outcome.evaluate) {
            const evaluated = await call('/evaluate', 'POST');
            if (!valid()) return false;
            if (evaluated?.enabled === false || evaluated?.skipped === 'DISABLED') { setNotice('평가 기능이 비활성화되었습니다.'); await readConfig(); return false; }
            if (evaluated?.skipped && valid()) setNotice((previous) => previous + ' 평가 저장이 생략되었습니다. 최신 검사 결과를 확인해 주세요.');
            // A skipped or NONE evaluation is not a declaration of normal settlement.
          }
        } catch (e) {
          operationError = '잔액 조회 또는 평가 처리 결과를 확인할 수 없습니다. 자동 재전송하지 않습니다.';
          if (valid()) setNotice(operationError);
        }
      }
      const next = await call('/preflight');
      if (!valid()) return false;
      if (!statuses.includes(next?.checkStatus)) throw Error('검사 응답 확인 불가');
      setResult(next); setError(operationError);
      if (next.enabled === false || next.checkStatus === 'DISABLED') { configRef.current = { ...cfg, enabled: false }; setConfig(configRef.current); }
      return !operationError;
    } catch (e) {
      if (valid()) setError(configRef.current ? '검사 결과 확인 불가 — 이전 결과는 최신 상태가 아닙니다.' : '검사 설정 확인 실패');
      return false;
    } finally { if (task.current === controller) task.current = null; if (alive.current) setBusy(false); finish(); }
  }, [active]);
  const reload = useCallback(() => run(false, !configRef.current?.enabled), [run]);
  const refreshBalance = useCallback(() => run(true), [run]);
  useEffect(() => {
    if (!active) return;
    // Wait for an aborted predecessor to settle; never overlap requests.
    let stopped = false;
    const enter = async () => {
      if (task.current) await task.current.finished;
      if (!stopped) await run(false, false, !visibleScope.current);
    };
    enter();
    const visibility = () => { if (document.hidden) task.current?.abort(); else enter(); };
    document.addEventListener('visibilitychange', visibility);
    const timer = polling ? setInterval(() => run(), 60000) : null;
    return () => { stopped = true; task.current?.abort(); clearInterval(timer); document.removeEventListener('visibilitychange', visibility); };
  }, [active, polling, run]);
  return { active, config, result, error, notice, busy, reload, refreshBalance };
}
